/**
 * /login — public route.
 *
 * Two redirect mechanisms, because they cover different moments:
 *
 *   1. `beforeLoad` (synchronous, runs on every navigation TO /login):
 *      if the store already says authenticated at nav time, bounce to
 *      /boards. This covers in-app links and reloads after the store
 *      rehydrated.
 *
 *   2. A reactive store subscription (item 12, 2026-06-11): AuthBootstrap
 *      resolves GET /api/auth/me *asynchronously*. A PWA cold-start can
 *      land a logged-in user on /login while the store is still
 *      idle/loading; `beforeLoad` already ran and does NOT re-run when the
 *      store later flips to authenticated. So we subscribe to the store
 *      and, the moment it transitions to `authenticated` *while the user
 *      is on /login*, move them to /boards — matching the founder landing.
 *
 * The reactive redirect lives at module scope (not in a component) on
 * purpose: the route's component is code-split into ./login.lazy.tsx, and
 * a lazy route's `component` overrides any component defined here, so a
 * `useEffect` hook in this file would never mount. There is also no
 * exported router singleton to call `navigate` on from module scope, so
 * we use the same full-document navigation the rest of the app already
 * uses for cross-context routing (see the sidebar logout handlers). This
 * only ever fires in the rare cold-start-on-/login-while-authenticated
 * window; the normal login flow navigates via the form's onSuccess.
 */
import { createFileRoute, redirect } from '@tanstack/react-router';

import { useAuthStore } from '../stores/auth-store';

// Guarded, one-way reactive redirect: only when the store transitions
// INTO `authenticated` AND the browser is currently sitting on /login.
useAuthStore.subscribe((state, prev) => {
  if (state.status !== 'authenticated' || prev.status === 'authenticated') return;
  if (typeof window === 'undefined') return;
  if (window.location.pathname !== '/login') return;
  window.location.assign('/boards');
});

export const Route = createFileRoute('/login')({
  beforeLoad: () => {
    if (useAuthStore.getState().status === 'authenticated') {
      throw redirect({ to: '/boards' });
    }
  },
}).lazy(() => import('./login.lazy').then((d) => d.Route));
